/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { cssMap as cssMapCompiled } from '@compiled/react';
import { di } from 'react-magnetic-di';

import { jsx } from '@atlaskit/css';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { getActionPresentation } from '../../controllers/active-action/getActionPresentation';
import { useActiveActionSeededFollowUp } from '../../controllers/active-action/useActiveActionSeededFollowUp';
import type { ConversationAssistantUIModes } from '../../types';

import { ConversationActionView } from './rovo-empty-state/conversation-action-view';
import { StoreFollowUpView } from './store-follow-up';

const styles = cssMapCompiled({
	fullScreenCentered: {
		maxWidth: 'min(800px, 50vw)',
		marginInline: 'auto',
	},
});

export const ConversationActionContainer = ({
	action,
	mode,
	fullScreen,
	agent,
	storeUserMessages,
	webSearchEnabled,
	addCurrentPageAsContextUserPreference,
	appFilters,
}: {
	action: ConversationAction;
	mode: ConversationAssistantUIModes;
	fullScreen: boolean;
	agent?: Agent;
	storeUserMessages: boolean;
	webSearchEnabled?: boolean;
	addCurrentPageAsContextUserPreference?: boolean;
	appFilters?: RovoApp[];
}): JSX.Element => {
	di(ConversationActionView, useActiveActionSeededFollowUp);

	const actionPresentation = getActionPresentation(action);
	const shouldCenterAction = Boolean(fullScreen && actionPresentation.centerInFullScreen);
	const { followUp, onFollowUpClick } = useActiveActionSeededFollowUp({
		activeAction: action,
		agent,
		storeUserMessages,
		webSearchEnabled,
		addCurrentPageAsContextUserPreference,
		appFilters,
	});

	return (
		<div css={[shouldCenterAction && styles.fullScreenCentered]}>
			<ConversationActionView
				action={action}
				mode={mode}
				hideActionHeader={actionPresentation.hideActionHeader}
				footer={
					followUp ? (
						<StoreFollowUpView followUp={followUp} onFollowUpClick={onFollowUpClick} />
					) : null
				}
			/>
		</div>
	);
};
